import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { departmentService } from '../../services/departmentService';
import LoadingSpinner from '../common/LoadingSpinner';
import { EmptyState } from '../common/EmptyState';
import { SECTORS } from '../../constants/authConstants';
import { 
  generateDepartmentCode, 
  getNextPriority,
  DEPARTMENT_STATUS 
} from '../../constants/departmentConstants';
import { debounce } from '../../utils/helpers';

const DepartmentManagement = () => {
  const { user, getUserRole } = useAuth();
  const { isMobile, getResponsiveValue } = useTheme();
  
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState({});
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Responsive configurations
  const titleSize = getResponsiveValue({
    mobile: 'text-xl',
    desktop: 'text-2xl'
  });

  const cardPadding = getResponsiveValue({
    mobile: 'p-4',
    desktop: 'p-6'
  });

  useEffect(() => {
    loadDepartments();
  }, []);

  const loadDepartments = async () => {
    try {
      setLoading(true);
      setError('');
      
      const allDepartments = await departmentService.getAllDepartments();
      
      // Ensure all priorities are numbers
      const departmentsWithNumericPriority = allDepartments.map(dept => ({
        ...dept,
        priority: dept.priority !== undefined && dept.priority !== null && dept.priority !== '' 
          ? Number(dept.priority) 
          : 999
      }));
      
      setDepartments(departmentsWithNumericPriority);
    } catch (error) {
      console.error('Error loading departments:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Debounced auto-save function
  const debouncedAutoSave = useCallback(
    debounce(async (departmentId, departmentData) => {
      try {
        setSaving(prev => ({ ...prev, [departmentId]: true }));
        
        await departmentService.updateDepartment(departmentId, departmentData, user.uid);
        
        setSaving(prev => ({ ...prev, [departmentId]: false }));
        
        // Update the department in local state
        setDepartments(prev => prev.map(dept => 
          dept.id === departmentId 
            ? { 
                ...dept, 
                ...departmentData,
                updatedAt: new Date() 
              }
            : dept
        ));
      } catch (error) {
        console.error('Auto-save error:', error);
        setSaving(prev => ({ ...prev, [departmentId]: false }));
        setErrors(prev => ({
          ...prev,
          [`${departmentId}.general`]: error.message
        }));
      }
    }, 1000),
    [user.uid]
  );

  const handleAddNew = async () => {
    try {
      const newDepartment = {
        sector: '',
        departmentName: `New Department ${departments.length + 1}`,
        departmentCode: '',
        priority: 1,
        isActive: true
      };
      
      const result = await departmentService.createDepartment(newDepartment, user.uid);
      
      setDepartments(prev => [result, ...prev]);
      
      setSuccess(true);
      setSuccessMessage('New department added successfully!');
      setTimeout(() => {
        setSuccess(false);
        setSuccessMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error in handleAddNew:', error);
      setError('Failed to add new department: ' + error.message);
    }
  };

  const handleDelete = async (departmentId) => {
    if (!confirm('Are you sure you want to delete this department?')) return;
    
    try {
      await departmentService.deleteDepartment(departmentId);
      
      setDepartments(prev => prev.filter(d => d.id !== departmentId));
      
      setSuccess(true);
      setSuccessMessage('Department deleted successfully!');
      setTimeout(() => {
        setSuccess(false);
        setSuccessMessage('');
      }, 3000);
    } catch (error) {
      setError('Failed to delete department');
    }
  };

  const handleFieldChange = (departmentId, field, value) => {
    let processedValue = value;
    
    // Handle special processing
    if (field === 'priority') {
      // Allow empty string while user is typing
      if (value === '' || value === null) {
        processedValue = '';
      } else {
        const numValue = parseInt(value);
        // Only validate if it's a valid number
        if (!isNaN(numValue)) {
          processedValue = Math.max(1, Math.min(999, numValue));
        } else {
          processedValue = '';
        }
      }
    }

    if (field === 'isActive') {
      processedValue = value === 'true' || value === true;
    }

    // Update departments state immediately
    setDepartments(prev => prev.map(dept => {
      if (dept.id !== departmentId) return dept;
      
      const updated = { ...dept, [field]: processedValue };
      
      // Auto-generate department code when name changes
      if (field === 'departmentName') {
        updated.departmentCode = generateDepartmentCode(processedValue);
      }
      
      return updated;
    }));

    // Clear field error
    setErrors(prev => ({
      ...prev,
      [`${departmentId}.${field}`]: ''
    }));

    // Find the current department
    const currentDept = departments.find(d => d.id === departmentId);
    if (!currentDept) return;

    const updatedDept = { ...currentDept, [field]: processedValue };
    
    // Auto-generate department code when name changes
    if (field === 'departmentName') {
      updatedDept.departmentCode = generateDepartmentCode(processedValue);
    }

    // Validation - skip if priority is empty (user is still typing)
    if (field === 'priority' && (processedValue === '' || processedValue === null)) {
      // Don't validate or auto-save while user is typing
      return;
    }

    const validation = departmentService.validateDepartment(updatedDept);
    
    if (!validation.isValid && validation.errors[field]) {
      setErrors(prev => ({
        ...prev,
        [`${departmentId}.${field}`]: validation.errors[field]
      }));
      return;
    }

    // Auto-save if value is meaningful
    if (value !== undefined && value !== null && value !== '') {
      debouncedAutoSave(departmentId, updatedDept);
    }
  };

  // Sort departments globally by priority, then group by sector for display
  const getSortedDepartmentsWithSectorHeaders = () => {
    if (!departments.length) return [];
    
    // Sort all departments by priority first
    const sortedDepartments = [...departments].sort((a, b) => {
      const priorityA = a.priority === '' || a.priority === null || a.priority === undefined 
        ? 999 
        : Number(a.priority) || 999;
      const priorityB = b.priority === '' || b.priority === null || b.priority === undefined 
        ? 999 
        : Number(b.priority) || 999;
      return priorityA - priorityB;
    });
    
    // Create rows with sector headers inserted where sector changes
    const rows = [];
    let currentSector = null;
    let sectorCounter = 1;
    
    sortedDepartments.forEach((dept) => {
      // Insert sector header when sector changes
      if (dept.sector !== currentSector) {
        currentSector = dept.sector;
        rows.push({
          type: 'sector-header',
          sector: dept.sector,
          sectorNumber: sectorCounter++
        });
      }
      
      // Add department row
      rows.push({
        type: 'department',
        department: dept
      });
    });
    
    return rows;
  };

  const getSectorLabel = (sectorValue) => {
    const sector = SECTORS.find(s => s.value === sectorValue);
    return sector ? sector.label : sectorValue;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-secondary flex items-center justify-center">
        <LoadingSpinner size="xl" text="Loading departments..." />
      </div>
    );
  }

  return (
    <div>
      {/* Success Message */}
      {success && (
        <div className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-3 shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <span className="ml-2 text-green-700 font-medium text-sm">{successMessage}</span>
          </div>
        </div>
      )}

      {/* Enhanced CSS */}
      <style>{`
        .no-arrows::-webkit-outer-spin-button,
        .no-arrows::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        
        .no-arrows[type=number] {
          -moz-appearance: textfield;
        }
        
        .enhanced-input:focus {
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        }
        
        .enhanced-input {
          transition: all 0.2s ease-in-out;
        }
        
        .enhanced-select {
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
          background-position: right 0.5rem center;
          background-repeat: no-repeat;
          background-size: 1.5em 1.5em;
          padding-right: 2.5rem;
        }
        
        .table-row:hover .enhanced-input {
          border-color: #3b82f6;
        }
      `}</style>

      {/* Header */}
      <div className="mb-6">
        <h1 className={`${titleSize} font-bold text-text-primary mb-2`}>Department Management</h1>
        <p className="text-text-secondary">
          Manage departments for each sector with priorities
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-error-subtle border border-border-error rounded-xl p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-error mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="text-error font-medium">{error}</span>
          </div>
        </div>
      )}

      {/* Departments Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {departments.length === 0 ? (
          <div className={cardPadding}>
            <EmptyState
              icon={
                <svg className="w-16 h-16 text-text-quaternary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              }
              title="No departments found"
              description="Start by adding your first department"
              actionLabel="Add First Department"
              onAction={handleAddNew}
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-16">
                    S. No.
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-48">
                    Sector
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-60">
                    Department Name
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-24">
                    Priority
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-32">
                    Status
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider w-20">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {/* Add New Item Row */}
                <tr className="bg-gradient-to-r from-blue-50 to-indigo-50 border-t-2 border-blue-200">
                  <td className="px-3 py-3 text-center border-r border-gray-200">
                    <button
                      onClick={handleAddNew}
                      className="group flex items-center justify-center w-8 h-8 bg-blue-500 hover:bg-blue-600 text-white rounded-full transition-all duration-200 transform hover:scale-110 shadow-lg hover:shadow-xl"
                      title="Add New Department"
                    >
                      <svg className="w-4 h-4 group-hover:rotate-90 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </button>
                  </td>
                  <td className="px-3 py-3 text-xs text-blue-600 font-medium border-r border-gray-200" colSpan="5">
                    Click + to add new department
                  </td>
                </tr>

                {/* Department Rows Sorted by Priority */}
                {getSortedDepartmentsWithSectorHeaders().map((row, index) => {
                  if (row.type === 'sector-header') {
                    // Sector Header Row
                    return (
                      <tr key={`header-${row.sector}-${index}`} className="bg-gray-100">
                        <td className="px-3 py-3 text-xs font-bold text-gray-900 border-r border-gray-200">
                          <div className="flex items-center justify-center w-6 h-6 bg-gray-200 rounded-full text-xs font-bold">
                            {row.sectorNumber}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-xs font-bold text-gray-900 uppercase border-r border-gray-200" colSpan="5">
                          {getSectorLabel(row.sector)}
                        </td>
                      </tr>
                    );
                  } else {
                    // Department Row
                    const dept = row.department;
                    const isSaving = saving[dept.id];

                    return (
                      <tr key={dept.id} className="table-row hover:bg-blue-50 transition-colors duration-200">
                        <td className="px-3 py-3 border-r border-gray-200"></td>
                        
                        {/* Sector Select */}
                        <td className="px-3 py-3 border-r border-gray-200">
                          <select
                            value={dept.sector || ''}
                            onChange={(e) => handleFieldChange(dept.id, 'sector', e.target.value)}
                            className={`enhanced-input enhanced-select w-full text-xs border border-gray-300 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none ${
                              errors[`${dept.id}.sector`] ? 'border-red-500 focus:ring-red-500' : ''
                            }`}
                          >
                            <option value="">Select Sector</option>
                            {SECTORS.map(sector => (
                              <option key={sector.value} value={sector.value}>
                                {sector.label}
                              </option>
                            ))}
                          </select>
                          {errors[`${dept.id}.sector`] && (
                            <div className="text-xs text-red-600 mt-0.5 font-medium">
                              {errors[`${dept.id}.sector`]}
                            </div>
                          )}
                          {isSaving && (
                            <div className="flex items-center mt-1">
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500"></div>
                              <span className="ml-1 text-xs text-blue-600">Saving...</span>
                            </div>
                          )}
                        </td>

                        {/* Department Name */}
                        <td className="px-3 py-3 border-r border-gray-200">
                          <input
                            type="text"
                            value={dept.departmentName || ''}
                            onChange={(e) => handleFieldChange(dept.id, 'departmentName', e.target.value)}
                            className={`enhanced-input w-full text-xs border border-gray-300 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                              errors[`${dept.id}.departmentName`] ? 'border-red-500 focus:ring-red-500' : ''
                            }`}
                            placeholder="Enter department name"
                          />
                          {errors[`${dept.id}.departmentName`] && (
                            <div className="text-xs text-red-600 mt-0.5 font-medium">
                              {errors[`${dept.id}.departmentName`]}
                            </div>
                          )}
                        </td>

                        {/* Priority */}
                        <td className="px-3 py-3 text-center border-r border-gray-200">
                          <input
                            type="number"
                            min="1"
                            max="999"
                            value={dept.priority === '' ? '' : dept.priority || ''}
                            onChange={(e) => handleFieldChange(dept.id, 'priority', e.target.value)}
                            className={`enhanced-input no-arrows w-full text-xs text-center border border-gray-300 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                              errors[`${dept.id}.priority`] ? 'border-red-500 focus:ring-red-500' : ''
                            }`}
                          />
                          {errors[`${dept.id}.priority`] && (
                            <div className="text-xs text-red-600 mt-0.5 font-medium">
                              {errors[`${dept.id}.priority`]}
                            </div>
                          )}
                        </td>

                        {/* Status Toggle */}
                        <td className="px-3 py-3 text-center border-r border-gray-200">
                          <select
                            value={dept.isActive ? 'true' : 'false'}
                            onChange={(e) => handleFieldChange(dept.id, 'isActive', e.target.value)}
                            className={`enhanced-input enhanced-select w-full text-xs border rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none ${
                              dept.isActive 
                                ? 'border-green-300 bg-green-50 text-green-700' 
                                : 'border-gray-300 bg-gray-50 text-gray-600'
                            }`}
                          >
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                          </select>
                        </td>

                        {/* Delete Action */}
                        <td className="px-3 py-3 text-center">
                          <button
                            onClick={() => handleDelete(dept.id)}
                            className="p-1 rounded-lg hover:bg-red-50 text-red-600 hover:text-red-700 transition-colors"
                            title="Delete Department"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    );
                  }
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DepartmentManagement;